import { useSetContainerValues } from '@/components/container/context/context';
import { SkeletonWoo } from '@/components/skeleton/skeleton.woo';
import { formatPhone } from '@/lib';
import { type UsernameAlias } from '@/portal-api.definition';
import { WooChevronRight } from '@nosinovacao/fe-icons';
import { ActionCard, useI18n } from '@nosinovacao/nosid-mfe-common';
import { Link, Outlet } from '@tanstack/react-router';
import { type HomePageProps } from '../-model/props';

export function HomePageWoo({ isLoading, usernameAliases }: Readonly<HomePageProps>) {
  const { t } = useI18n();
  useSetContainerValues({
    title: t('Home.Title'),
  });

  return (
    <>
      <main className="grid gap-2 desktop-md:grid-cols-[1fr_minmax(21.25rem,max-content)] desktop-md:gap-x-6 desktop-md:gap-y-4">
        <SkeletonWoo loading={isLoading} className="h-48 w-full">
          <UsernameAliasCard aliases={usernameAliases} />
        </SkeletonWoo>
        <div className="flex flex-col gap-2">
          <SkeletonWoo loading={isLoading} className="h-[92px] w-full">
            <ChangePasswordCard />
          </SkeletonWoo>
        </div>
      </main>
      <Outlet />
    </>
  );
}

function UsernameAliasCard({ aliases: usernameAliases }: Readonly<{ aliases: UsernameAlias[] }>) {
  const { t } = useI18n();
  const emailAlias = usernameAliases.find((al) => al.Type === 'Email');
  const mobileAlias = usernameAliases.find((al) => al.Type === 'Mobile');
  const otherAlias = usernameAliases.find((al) => al.Type === 'Other');
  const aliases: UsernameAlias[] = [
    {
      Type: 'Mobile',
      Username: mobileAlias?.Username ? formatPhone(mobileAlias.Username) : '',
    },
    {
      Type: 'Email',
      Username: emailAlias?.Username ?? '',
    },
  ];

  if (otherAlias) {
    aliases.push({
      Type: 'Other',
      Username: otherAlias.Username,
    });
  }
  return (
    <div className="grid gap-5 border border-nos-neutral-100 bg-nos-neutral-0 p-4 shadow-1">
      <span
        className="font-medium text-nos-neutral-900 font-title-h4"
        data-tid="title-login-aliases"
      >
        {t('Home.LoginUsernameAliases')}
      </span>
      {aliases.map((al) => (
        <div className="grid gap-1" key={al.Type}>
          <span className="text-nos-neutral-500 font-text-p2">
            {t(`Home.UsernameAlias.${al.Type}`)}
          </span>
          <span
            className="text-nos-neutral-900 font-text-p1"
            data-tid={`text-alias-${al.Type.toLowerCase()}`}
          >
            {al.Username || t('Home.UsernameAlias.Default')}
          </span>
        </div>
      ))}
    </div>
  );
}

function ChangePasswordCard() {
  const { t } = useI18n();
  return (
    <ActionCard
      skin="woo"
      variant="quick-link"
      preTitle={t('Home.Password')}
      subTitle={<></>}
      title={t('Home.ActionChange')}
      icon={<WooChevronRight width={20} height={20} />}
      className="w-full border-nos-neutral-200 bg-nos-neutral-0"
      data-tid="card-change-password"
      asChild
    >
      <Link to="/home/change-password" />
    </ActionCard>
  );
}
